import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button,
  DatePicker,
  DatePickerProps,
  Flex,
  Input,
  Modal,
  Popconfirm,
  Radio,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd'
import dayjs from 'dayjs'
import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { recallCredits, scheduleCredits, unscheduleCredits, workspaceQueries } from '../../../../../Actions'
import {
  SCHEDULE_STATUS_COLOR,
  SCHEDULE_STATUS_LABEL,
  TransactionSchedule,
  TransactionScheduleStatus,
} from '../../../../../types/credit'

type RechargeType = 'IMMEDIATE' | 'SCHEDULED'

type Props = {
  workspaceId: number
}

export default function RetrieveCredits({ workspaceId }: Props) {
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [rechargeType, setRechargeType] = useState<RechargeType>('IMMEDIATE')
  const [rechargeAmount, setRechargeAmount] = useState('')
  const [scheduleDate, setScheculeDate] = useState<DatePickerProps['value']>()
  const [modalMessage, setModalMessage] = useState('')
  const [messageApi, contextHolder] = message.useMessage()
  const queryClient = useQueryClient()
  const { mutateAsync: mutateToRecallCredits } = useMutation({ mutationFn: recallCredits })
  const { mutateAsync: mutateToScheduleCredits } = useMutation({ mutationFn: scheduleCredits })
  const { mutate: mutateToUnscheduleCredits } = useMutation({ mutationFn: unscheduleCredits })
  // status 미지정 = 4상태 전부. 실행·취소된 예약도 목록에 남는다
  const { data, refetch: refetchScheduledCreditsList } = useQuery({
    ...workspaceQueries.detail(workspaceId)._ctx.scheduleCredits({ max: 0 }),
  })
  const [isLoading, setIsLoading] = useState(false)

  const showModal = () => {
    setIsModalVisible(true)
  }

  const handleCancel = () => {
    setIsModalVisible(false)
  }

  const handleRecharge = async () => {
    // 검증은 try 밖에서 한다. finally 가 모달을 닫고 메시지를 지우므로 안에서 알리면 보이지 않는다
    if (!rechargeAmount) {
      setModalMessage('크레딧을 입력해주세요.')
      return
    }
    if (rechargeType === 'SCHEDULED') {
      if (!scheduleDate) {
        setModalMessage('예약일시를 적어주세요.')
        return
      }
      // 플랜 부여/회수 예약과 같은 기준. 과거 시각도 여기서 함께 걸린다.
      // 과거 시각은 백엔드가 검증 없이 받아 Quartz 가 즉시 발화시킨다
      if (scheduleDate.isBefore(dayjs().add(5, 'minutes'))) {
        setModalMessage('예약일시를 현재의 5분 이후로 설정해주세요.')
        return
      }
    }

    // 회수 API 는 양수를 받는다. 예약은 기존대로 음수 예약으로 저장한다
    const amount = parseFloat(rechargeAmount)

    try {
      setIsLoading(true)

      if (rechargeType === 'SCHEDULED' && scheduleDate) {
        await mutateToScheduleCredits({
          workspaceId,
          amount: -amount,
          memo: '어드민 회수',
          scheduledAt: scheduleDate.format('YYYY-MM-DDTHH:mm:ss'),
        })
        messageApi.success('회수를 예약했습니다.')
      } else {
        const result = await mutateToRecallCredits({
          workspaceId,
          amount,
          memo: '어드민 회수',
        })
        if (result.shortfall > 0) {
          messageApi.warning(
            `어드민 부여 잔량이 부족해 ${result.recalled.toLocaleString()} 크레딧만 회수했습니다.` +
              ` (요청 ${result.requested.toLocaleString()}, 부족 ${result.shortfall.toLocaleString()})`,
          )
        } else {
          messageApi.success(`${result.recalled.toLocaleString()} 크레딧을 회수했습니다.`)
        }
      }

      // 잔액·출처별 잔량·원장·예약 목록이 한꺼번에 바뀐다
      await queryClient.invalidateQueries({ queryKey: workspaceQueries.detail(workspaceId).queryKey })
    } catch (e) {
      messageApi.error(e instanceof Error ? e.message : '크레딧 회수에 실패했습니다.')
    } finally {
      // cleanup
      setIsModalVisible(false)
      setRechargeAmount('0')
      setModalMessage('')
      setIsLoading(false)
      await refetchScheduledCreditsList()
    }
  }

  const handleScheduleChanged = (value: DatePickerProps['value']) => {
    setScheculeDate(value)
  }

  return (
    <>
      {contextHolder}
      <div>
        <Flex justify="flex-start">
          <Button onClick={showModal}>회수하기</Button>
        </Flex>

        <Typography.Title level={5}>회수 예약 내역</Typography.Title>
        <Table
          dataSource={data?.content}
          rowKey={(record: TransactionSchedule) => record.id}
          pagination={false}
          columns={[
            {
              title: 'scheduledAt',
              dataIndex: 'scheduledAt',
              key: 'scheduledAt',
            },
            {
              title: 'amount',
              dataIndex: 'amount',
              key: 'amount',
            },
            {
              title: '상태',
              dataIndex: 'status',
              key: 'status',
              render: (status: TransactionScheduleStatus) =>
                status ? <Tag color={SCHEDULE_STATUS_COLOR[status]}>{SCHEDULE_STATUS_LABEL[status]}</Tag> : '-',
            },
            {
              title: '회수량',
              key: 'executedAmount',
              render: (t, r: TransactionSchedule) => {
                if (r.status !== 'COMPLETED') return '-'
                // COMPLETED + transactionId 없음 = 회수 0 (0원 회수 행을 남기지 않는다). 결과 미상과 구분한다
                if (r.executedAmount === null || r.executedAmount === undefined) {
                  return r.transactionId === null ? (
                    '0 (회수 없음)'
                  ) : (
                    <Typography.Text type="secondary">결과 미상</Typography.Text>
                  )
                }
                return Math.abs(Number(r.executedAmount))
              },
            },
            {
              title: '부족분',
              key: 'shortfall',
              render: (t, r: TransactionSchedule) => {
                const shortfall = Number(r.shortfall ?? 0)
                if (r.status !== 'COMPLETED' || r.shortfall === null || r.shortfall === undefined) return '-'
                if (shortfall <= 0) return 0
                return (
                  <Tooltip title="실행 시점 상한(어드민 부여 풀 잔량 − 예약 생성 이후 부여분)에 걸려 그만큼 회수되지 않았습니다.">
                    <Typography.Text type="warning">{shortfall}</Typography.Text>
                  </Tooltip>
                )
              },
            },
            {
              title: '원장',
              dataIndex: 'transactionId',
              key: 'transactionId',
              render: (transactionId: number | null) => transactionId ?? '-',
            },
            {
              title: 'issuerEmail',
              dataIndex: 'issuerEmail',
              key: 'issuerEmail',
              render: (t, r: any) => <Link to={`/user/${r.issuerId}`}>{r.issuerEmail}</Link>,
            },
            {
              width: 100,
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              render: (t, r: TransactionSchedule) =>
                // 예약취소는 미실행 건만. 다른 상태는 백엔드가 IllegalStateException 을 던진다
                r.status === 'PENDING' ? (
                  <Popconfirm
                    title="예약취소"
                    description="예약을 취소하시겠습니까?"
                    onConfirm={() => {
                      mutateToUnscheduleCredits(r.id, { onSuccess: () => refetchScheduledCreditsList() })
                    }}
                    okText="예"
                    cancelText="아니오"
                  >
                    <Button>예약취소</Button>
                  </Popconfirm>
                ) : null,
            },
          ]}
          scroll={{ y: 65 * 10 }}
        />
      </div>
      <Modal
        title="크레딧 회수"
        open={isModalVisible}
        onOk={handleRecharge}
        onCancel={handleCancel}
        loading={isLoading}
      >
        <Space direction="vertical" size="middle">
          <Input
            style={{ marginTop: '16px' }}
            type="number"
            value={rechargeAmount}
            min={0}
            placeholder="회수할 크레딧"
            onChange={(e) => setRechargeAmount(e.target.value)}
          />
          <Radio.Group value={rechargeType} onChange={(e) => setRechargeType(e.target.value)}>
            <Space direction="vertical">
              <Radio value={'IMMEDIATE' satisfies RechargeType}>즉시 회수하기</Radio>
              <Radio value={'SCHEDULED' satisfies RechargeType}>회수 예약하기</Radio>
            </Space>
          </Radio.Group>
          {rechargeType === 'SCHEDULED' ? (
            <DatePicker
              showTime
              onChange={(value, dateString) => {
                console.log('Selected Time: ', value)
                console.log('Formatted Selected Time: ', dateString)
                handleScheduleChanged(value)
              }}
              // onOk={handleScheduleChanged}
              value={scheduleDate}
            />
          ) : null}
          <Typography.Text type="secondary">
            회수 대상은 어드민 부여분입니다. 구독·쿠폰·단건 구매분은 회수되지 않습니다.
          </Typography.Text>
          {modalMessage ? <Typography.Text type="danger">{modalMessage}</Typography.Text> : null}
        </Space>
      </Modal>
    </>
  )
}
